// @givmo/fundraisers-module — the console's season setup (FCM-WEB34, plan §FC-16; the mockups Jonathan ruled
// as D-FCM-61, all eight decisions as drawn, `scratchpad/2026-10-22-fcm-web34-season-setup-mockups/`, as
// D-GFU-57 amended them, with D-FCM-65's Terms of Use line). A season is a campaign like any other; this
// page, opened from its Layers tab, makes its games in one act. Its three calls, in FCM-BE119's documented
// order: the season's settings (`PATCH …/campaign`, its deadline and what it calls the campaigns inside it),
// its pledge template (`PUT …/pledge-template`), then the games (`POST …/campaign/season`), one transaction
// per game. The act itself makes each game's nightly fundraiser the one its page features, and on the
// season's first setup sets its card's look to Schedule; the page sets neither.
//
// "Add games" is the same page on a season already set up: new games take its stored pledge template, so
// only the schedule shows (and, under a season whose supporters pick, each nightly fundraiser's picks,
// which every act needs). A partial answer keeps the games the act didn't make on the page, each with the
// server's own sentence, and offers to try them again (decision 6). A refusal is the server's sentence in
// the module's toast, the form keeping what was typed. Built once for both consoles: the class enters only
// as the server's data.
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  firstErrorCode,
  idempotencyKey,
  isGivmoError,
  sdk,
  type FundraiserOut,
  type OrgFundraiserOut,
  type P2pConfigOut,
  type P2pConfigRequest,
  type P2pSeasonSetupOut,
  type PledgeRuleSentencePreviewOut,
  type PledgeTemplateOut,
} from '@givmo/api-client';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  Field,
  Icon,
  IconButton,
  PageHeader,
  RadioTile,
  RadioTileGroup,
  Select,
  Textarea,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  ArrowLeft,
  CircleAlert,
  CircleCheck,
  IdCard,
  Info,
  Plus,
  Route,
  Trash2,
} from 'lucide-react';
import { useFundraisersModule } from './fundraisers-context';
import { mayWriteFundraisers } from './fundraisers-config';
import { fundraiserOptions, privateFundraiserOptions } from './fundraisers-queries';
import { campaignOptions } from './campaign-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import {
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
  ModuleLink,
} from './fundraisers-state';
import { Chips, idsOf, recipientKey } from './campaign-settings-cards';
import { DialogFrame } from './layer-dialogs';
import { LayersUnavailable } from './layers-tab';
import { PickerResults, RecipientSearch } from './recipient-picker';
import { useRecipientNames } from './recipient-names';
import { currentRecipients, type RecipientItem } from './recipients-decode';
import { LAYERS_COPY as C, series } from './layers-copy';
import {
  LAYER_NAME_MAX_LENGTH,
  collapseLayerName,
  deadlineBody,
  deadlineDay,
  layerNameBody,
  layerRefusal,
  deadlineZone,
  sameDay,
  storedLayerName,
  zoneName,
} from './layers-decode';
import { invalidateLayerReads } from './layers-queries';
import { INLINE_HIT } from './lifecycle-ui';
import { useLivePage } from './use-live-page';
import { SEASON_COPY as S } from './season-setup-copy';
import { centsShown } from './pledge-template-decode';
import {
  EMPTY_TERMS,
  TERMS_REFUSALS,
  askAgain,
  blank,
  complete,
  dayOf,
  gameNames,
  keepDayTogether,
  lastDay,
  missingTerms,
  previewBody,
  previewLines,
  refusedRows,
  seasonBody,
  seasonLive,
  shortDay,
  storedUnits,
  templateBody,
  termsOfTemplate,
  unitsOf,
  type CurrentTerms,
  type GameRow,
  type RatePolicy,
  type SeasonTerms,
} from './season-setup-decode';
import {
  currentTermsOptions,
  pledgeTemplateOptions,
  rulePreviewOptions,
} from './season-setup-queries';

/** How long the page waits before asking the act again, while no answer has come (decision 6: the page
 * waits once; a lost answer is asked for again under the same key, which replays it once it is ready). */
const ASK_AGAIN_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 15_000, 15_000, 15_000, 15_000, 15_000];

async function untilAnswered<T>(ask: () => Promise<T>, live: () => boolean): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await ask();
    } catch (error) {
      const wait = ASK_AGAIN_MS[attempt];
      if (!askAgain(error) || wait === undefined || !live()) throw error;
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  }
}

/** A value once it has stopped changing for `ms`, so the server's preview isn't asked at every key. */
export function useSettled<T>(value: T, ms: number): T {
  const text = JSON.stringify(value) ?? 'null';
  const [settled, setSettled] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(text), ms);
    return () => clearTimeout(timer);
  }, [text, ms]);
  return useMemo(() => JSON.parse(settled) as T, [settled]);
}

/** A game's page as its row names it while typed: the matchup and its day (decision 8), the matchup
 * alone before a day, nothing before an opponent. */
function rowName(subject: string, game: GameRow): string | null {
  if (game.opponent.trim() === '') return null;
  if (game.day === '') return S.vsName(subject || '…', game.opponent.trim());
  return gameNames(subject || '…', game).name;
}

/** A picked day as the schedule keeps it: "2027-04-03", in the reader's zone. */
function dayText(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const TIME_LABEL = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** First pitch, chosen from the day's times five minutes apart ("7:05 PM"), kept as "19:05". */
const TIME_OPTIONS = Array.from({ length: 24 * 12 }, (_, step) => {
  const hours = Math.floor(step / 12);
  const minutes = (step % 12) * 5;
  return {
    value: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    label: TIME_LABEL.format(new Date(2000, 0, 1, hours, minutes)),
  };
});

/** The way back from this page: to the season's Layers tab, by its name. */
function SeasonBack({ fundraiserId, name }: { fundraiserId: string; name: string }) {
  const module = useFundraisersModule();
  return (
    <div>
      <Button
        appearance="saas"
        tone="ghost"
        size="sm"
        leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
        onClick={() => module.onNavigate(FUNDRAISERS_ROUTES.layers(fundraiserId))}
        testId="season-back"
      >
        {name}
      </Button>
    </div>
  );
}

export function SeasonSetupPage({ fundraiserId }: { fundraiserId: string }) {
  const config = useFundraisersModule();
  return (
    <LoadedSeasonSetup
      key={`${config.orgRef ?? 'pending'}:${fundraiserId}`}
      fundraiserId={fundraiserId}
    />
  );
}

function LoadedSeasonSetup({ fundraiserId }: { fundraiserId: string }) {
  const module = useFundraisersModule();
  const writable = mayWriteFundraisers(module);
  const row = useQuery({
    ...fundraiserOptions(module.orgRef ?? '', fundraiserId),
    enabled: module.orgRef !== null && writable,
  });
  // The act, the template and the season's settings are the season's OWNER rank's.
  const owner = row.data?.effective_fundraiser_role === 'OWNER';
  const scoped = row.isSuccess && owner;
  const own = useQuery({ ...privateFundraiserOptions(fundraiserId), enabled: scoped });
  const overview = useQuery({ ...campaignOptions(fundraiserId), enabled: scoped, retry: false });
  const live = seasonLive(overview.data?.config);
  const template = useQuery({
    ...pledgeTemplateOptions(fundraiserId),
    enabled: scoped && live,
    retry: false,
  });

  // A way back from every state this page can't serve: the season by its name where it was read, else the
  // module's own "Back to Fundraisers".
  if (module.orgRef !== null && !writable)
    return (
      <FundraisersPageShell testId="page-season-setup">
        <div>
          <ModuleLink href={FUNDRAISERS_ROUTES.list} testId="season-back-to-fundraisers">
            Back to Fundraisers
          </ModuleLink>
        </div>
        <FundraisersReadState denied onRetry={() => undefined} />
      </FundraisersPageShell>
    );
  if (!module.orgRef || row.isPending) return <FundraisersLoading />;
  if (row.isError)
    return (
      <FundraisersReadState
        error={row.error}
        detail
        fundraiserId={fundraiserId}
        onRetry={() => void row.refetch()}
      />
    );
  if (!owner)
    return (
      <FundraisersPageShell testId="page-season-setup">
        <SeasonBack fundraiserId={fundraiserId} name={row.data.name} />
        <FundraisersReadState denied onRetry={() => void row.refetch()} />
      </FundraisersPageShell>
    );
  if (overview.isPending || own.isPending || (live && template.isPending))
    return <FundraisersLoading />;
  if (overview.isError || own.isError || template.isError || !live)
    return (
      <FundraisersPageShell testId="page-season-setup">
        <SeasonBack fundraiserId={fundraiserId} name={row.data.name} />
        <LayersUnavailable
          onRetry={() => {
            void overview.refetch();
            void own.refetch();
            void template.refetch();
          }}
        />
      </FundraisersPageShell>
    );
  return (
    <SeasonForm
      row={row.data}
      own={own.data}
      config={overview.data.config}
      layerCount={overview.data.layers?.length ?? 0}
      template={template.data ?? null}
    />
  );
}

function SeasonForm({
  row,
  own,
  config,
  layerCount,
  template,
}: {
  row: OrgFundraiserOut;
  own: FundraiserOut;
  config: P2pConfigOut;
  /** The campaigns already inside the season. */
  layerCount: number;
  template: PledgeTemplateOut | null;
}) {
  const module = useFundraisersModule();
  // The season's games, its deadline and its pledges are set on the organization's own clock, else
  // the reader's, named on the page.
  const seasonZone = deadlineZone(module.orgTimeZone);
  const client = useQueryClient();
  const live = useLivePage();
  const fundraiserId = row.id;
  const season = row.name;
  // "Add games" (decision 1): a season set up carries a template naming whom it counts, and games
  // under it. Decided once, so the page keeps its shape while its own act changes what it read.
  const [adding] = useState(
    () => layerCount > 0 && (template?.measure.subject_name ?? '').trim() !== '',
  );
  const storedDay = useMemo(
    () =>
      config.deadline_at ? deadlineDay(config.deadline_at, config.deadline_time_zone) : undefined,
    [config.deadline_at, config.deadline_time_zone],
  );
  const storedName = storedLayerName(config);
  const picksOwn = config.recipient_choice === 'supporters';
  const binding = picksOwn ? (config.binding_list ?? null) : null;
  const listNames = useRecipientNames(binding, binding !== null);
  const ownItems = currentRecipients(own);

  const [games, setGames] = useState<GameRow[]>([]);
  const nextKey = useRef(1);
  const [terms, setTerms] = useState<SeasonTerms>(() => termsOfTemplate(template) ?? EMPTY_TERMS);
  const set: TermsSetter = (key, value) => setTerms((now) => ({ ...now, [key]: value }));
  // The deadline follows the last game's day until the organizer sets one (or the season has one).
  const [deadline, setDeadline] = useState<Date | undefined>(storedDay);
  const [deadlineSet, setDeadlineSet] = useState(storedDay !== undefined);
  const [deadlineEdited, setDeadlineEdited] = useState(false);
  const [singular, setSingular] = useState(storedName?.singular ?? S.gameSingular);
  const [plural, setPlural] = useState(storedName?.plural ?? S.gamePlural);
  const [picks, setPicks] = useState<RecipientItem[]>([]);
  // A press was made: a half-typed game, a missing field and a missing pick say so.
  const [shown, setShown] = useState(false);
  // FCM-BE119's refusals by row: the server's own sentence, or null for a fault.
  const [reasons, setReasons] = useState<ReadonlyMap<number, string | null>>(new Map());
  // The latest answer's numbers, fixed when it came (review-88's F2): the games made so far, and how many of
  // the latest act's it couldn't make.
  const [partly, setPartly] = useState<{ made: number; left: number } | null>(null);
  // A refusal that stopped the whole act, in the server's own words, kept by the act until the form changes
  // (the toast fades and leaves nothing behind).
  const [actRefusal, setActRefusal] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const termsRead = useQuery({ ...currentTermsOptions(), retry: false });
  const fieldRefs = {
    subject: useRef<HTMLInputElement>(null),
    unit: useRef<HTMLInputElement>(null),
    most: useRef<HTMLInputElement>(null),
    rate: useRef<HTMLInputElement>(null),
    cap: useRef<HTMLInputElement>(null),
  };
  const picksRef = useRef<HTMLDivElement>(null);

  // The season's settings and its terms show until the act has made games; then only what's left to try.
  const setupCards = !adding && partly === null;
  const ready = games.filter(complete);
  const shownDeadline = deadlineSet ? deadline : lastDay(games);
  const subject = adding ? (template?.measure.subject_name ?? '').trim() : terms.subject.trim();
  const units = adding ? storedUnits(template) : unitsOf(terms.unit);
  const missing = setupCards ? missingTerms(terms) : [];
  const halfDone = games.filter((game) => !complete(game) && !blank(game));
  const needsPicks = picksOwn && picks.length === 0;
  const termsNow = termsRead.data ?? null;
  const canSave =
    ready.length > 0 &&
    halfDone.length === 0 &&
    missing.length === 0 &&
    !needsPicks &&
    termsNow !== null;

  const previewAsk = useSettled(
    setupCards && missing.length === 0
      ? previewBody(fundraiserId, terms, ready[0], seasonZone)
      : null,
    400,
  );
  const preview = useQuery(rulePreviewOptions(previewAsk));
  // "Add games": the season's pledge as stored, in the server's own sentence for these games.
  const storedTerms = adding ? termsOfTemplate(template) : null;
  const storedAsk = useSettled(
    storedTerms ? previewBody(fundraiserId, storedTerms, ready[0], seasonZone) : null,
    400,
  );
  const storedPreview = useQuery(rulePreviewOptions(storedAsk));
  const formKey = JSON.stringify([
    games,
    terms,
    deadline,
    singular,
    plural,
    picks.map(recipientKey),
  ]);
  useEffect(() => setActRefusal(null), [formKey]);

  /** The season's settings write: only what differs from what it stores. */
  function settingsBody(): P2pConfigRequest | null {
    const body: P2pConfigRequest = {};
    if (!sameDay(shownDeadline, storedDay))
      Object.assign(body, deadlineBody(shownDeadline, seasonZone));
    const name = layerNameBody(singular, plural).layer_name ?? null;
    const same =
      name === null
        ? storedName === null
        : storedName !== null &&
          name.singular === storedName.singular &&
          name.plural === storedName.plural;
    if (!same) body.layer_name = name;
    return Object.keys(body).length > 0 ? body : null;
  }

  const save = useMutation({
    mutationFn: async (): Promise<{ answer: P2pSeasonSetupOut; asked: GameRow[] }> => {
      const asked = games.filter(complete);
      const path = { fundraiser_id: fundraiserId };
      if (setupCards) {
        const settings = settingsBody();
        if (settings)
          await sdk.campaignsUpdateCampaign({ path, body: settings, throwOnError: true });
        await sdk.pledgesSetPledgeTemplate({
          path,
          body: templateBody(terms),
          ...idempotencyKey(crypto.randomUUID()),
          throwOnError: true,
        });
      }
      const body = seasonBody(
        asked,
        subject,
        seasonZone,
        termsNow!.id,
        picksOwn ? idsOf(picks) : null,
      );
      // One key for this press: a lost answer is asked for again under it, never a second act.
      const key = crypto.randomUUID();
      const { data } = await untilAnswered(
        () =>
          sdk.campaignsSetUpCampaignSeason({
            path,
            body,
            ...idempotencyKey(key),
            throwOnError: true,
          }),
        live,
      );
      return { answer: data, asked };
    },
    onSuccess: async ({ answer, asked }) => {
      const refused = refusedRows(asked, answer);
      const made = (partly?.made ?? 0) + answer.games.length;
      await Promise.all([
        invalidateLayerReads(client),
        client.invalidateQueries({ queryKey: campaignOptions(fundraiserId).queryKey }),
        client.invalidateQueries({ queryKey: pledgeTemplateOptions(fundraiserId).queryKey }),
      ]);
      if (!live()) return;
      if (refused.size === 0) {
        toast.success(S.gamesSetUp(made));
        module.onNavigate(FUNDRAISERS_ROUTES.layers(fundraiserId));
        return;
      }
      setGames((now) => now.filter((game) => !complete(game) || refused.has(game.key)));
      setReasons(refused);
      setPartly({ made, left: refused.size });
    },
    onError: (error) => {
      if (!live()) return;
      // D-FCM-65: the Terms changed (or none are current); read them again, and the line shows again.
      if (isGivmoError(error) && TERMS_REFUSALS.has(firstErrorCode(error) ?? ''))
        void termsRead.refetch();
      // An answer that never came, the page's asks run out (review-88's F3), is a fault: the module's own
      // line, never the server's word about its key.
      const fault =
        !isGivmoError(error) || error.isTransient || (error.status ?? 0) >= 500 || askAgain(error);
      const message = fault ? C.layerFailed : layerRefusal(error).message;
      toast.error(message);
      setActRefusal(fault ? null : message);
    },
  });

  function press() {
    setShown(true);
    if (canSave) {
      save.mutate();
      return;
    }
    const first = missing[0];
    if (first && first in fieldRefs) fieldRefs[first as keyof typeof fieldRefs].current?.focus();
    else if (needsPicks) picksRef.current?.scrollIntoView({ block: 'center' });
  }
  const remove = (game: GameRow) => {
    setGames((now) => now.filter((other) => other.key !== game.key));
    if (complete(game)) {
      // By its day: short enough to sit beside Undo, whatever the page is called (decision 8).
      toast(S.gameRemoved(keepDayTogether(shortDay(game.day))), {
        action: {
          label: S.undo,
          onClick: () => setGames((now) => [...now, game].sort((a, b) => a.key - b.key)),
        },
      });
    }
  };
  const add = () => {
    const key = nextKey.current;
    nextKey.current += 1;
    setGames((now) => [...now, { key, day: '', time: '', opponent: '' }]);
  };
  const back = () => module.onNavigate(FUNDRAISERS_ROUTES.layers(fundraiserId));
  const recipientLine =
    module.flavor.recipientMode === 'self'
      ? C.whoFixed(season, module.orgName)
      : picksOwn
        ? C.whoSupportersDesc
        : C.whoFixed(season, series(ownItems.map((item) => item.name)));
  const recipients = (
    <Recipients
      selfName={module.flavor.recipientMode === 'self' ? module.orgName : null}
      ownItems={picksOwn ? null : ownItems}
      listFrom={binding ? (binding.campaign_name ?? season) : null}
      picksOwn={picksOwn}
      picks={picks}
      listItems={listNames.items}
      listReady={listNames.ready}
      flagged={shown && needsPicks}
      boxRef={picksRef}
      onPick={(item) =>
        setPicks((now) =>
          now.some((p) => recipientKey(p) === recipientKey(item)) ? now : [...now, item],
        )
      }
      onUnpick={(item) =>
        setPicks((now) => now.filter((p) => recipientKey(p) !== recipientKey(item)))
      }
    />
  );

  return (
    <FundraisersPageShell testId="page-season-setup">
      {/* Phone: no word left alone at a block's end (tester-5's item 12). */}
      <div className="contents text-pretty">
        <SeasonBack fundraiserId={fundraiserId} name={season} />
        <PageHeader title={adding ? S.addGames : S.setUp} subtitle={S.subtitle(season)} />
        {partly && (
          <div
            role="status"
            className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12"
            data-testid="season-partly"
          >
            <Icon icon={Info} size={16} aria-hidden className="mt-1 shrink-0 text-text-secondary" />
            <p className="text-s13 font-w500 leading-h15 text-text-strong">
              {S.partlyNotice(partly.made, partly.made + partly.left, partly.left)}
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 items-start gap-24 xl:grid-cols-[minmax(0,1fr)_300px]">
          {/* While the act runs the form is held as it stood: an edit then would not be what was made. */}
          <fieldset
            disabled={save.isPending}
            className="m-0 flex min-w-0 flex-col gap-20 border-0 p-0"
            data-testid="season-form"
          >
            {setupCards && (
              <SeasonCard
                recipientLine={recipientLine}
                deadline={shownDeadline}
                followsGames={!deadlineSet && shownDeadline !== undefined}
                zone={
                  storedDay !== undefined && !deadlineEdited
                    ? config.deadline_time_zone
                    : seasonZone
                }
                onDeadline={(day) => {
                  setDeadline(day);
                  setDeadlineSet(true);
                  setDeadlineEdited(true);
                }}
                singular={singular}
                plural={plural}
                onSingular={setSingular}
                onPlural={setPlural}
              />
            )}
            <ScheduleCard
              games={games}
              subject={subject}
              shown={shown}
              reasons={reasons}
              onChange={(key, change) => {
                setGames((now) =>
                  now.map((game) => (game.key === key ? { ...game, ...change } : game)),
                );
                // A game changed after its refusal: its reason no longer says what's wrong with it.
                setReasons((now) => {
                  if (!now.has(key)) return now;
                  const next = new Map(now);
                  next.delete(key);
                  return next;
                });
              }}
              onRemove={remove}
              onAdd={add}
            />
            {setupCards ? (
              <>
                <ScoreboardCard
                  terms={terms}
                  set={set}
                  units={units}
                  flagged={shown ? missing : []}
                  refs={fieldRefs}
                />
                <NightlyCard
                  season={season}
                  terms={terms}
                  set={set}
                  flagged={shown ? missing : []}
                  refs={fieldRefs}
                  recipients={recipients}
                  preview={preview.data ?? null}
                  previewing={previewAsk !== null}
                  previewRefusal={
                    preview.isError && isGivmoError(preview.error) && preview.error.status === 400
                      ? layerRefusal(preview.error).message
                      : null
                  }
                  previewFailed={preview.isError}
                />
                <CardLevelCard season={season} />
              </>
            ) : (
              adding &&
              template && (
                // "Add games": the season's pledge as stored, which the new games take, read-only
                // (tester-5's item 9); and, under a season whose supporters pick, each nightly
                // fundraiser's picks in their own card, which every act names and this page has nowhere
                // else to make. (After a partial answer the setup's own terms and picks stand.)
                <>
                  <StoredPledgeCard
                    season={season}
                    template={template}
                    preview={storedPreview.data ?? null}
                    previewing={storedAsk !== null}
                    previewFailed={storedPreview.isError}
                  />
                  {picksOwn && (
                    <Card flush testId="season-nightly">
                      <CardHeader title={S.nightlyTitle} />
                      <CardBody className="flex flex-col gap-20">{recipients}</CardBody>
                    </Card>
                  )}
                </>
              )
            )}
          </fieldset>
          <div className="xl:sticky xl:top-[72px]">
            <SetupSummary
              season={season}
              units={units || S.unitsFallback}
              count={ready.length}
              rows={games.length}
              adding={adding}
              busy={save.isPending}
              partly={partly !== null}
              refusal={actRefusal}
              terms={termsNow}
              termsState={termsRead.isPending ? 'loading' : termsRead.isError ? 'failed' : 'ready'}
              onTermsRetry={() => void termsRead.refetch()}
              onTermsOpen={() => setReading(true)}
              onSave={press}
              onCancel={back}
            />
          </div>
        </div>
        {termsNow && <TermsReader terms={termsNow} open={reading} onOpenChange={setReading} />}
      </div>
    </FundraisersPageShell>
  );
}

// ── The season ──────────────────────────────────────────────────────────────────────────────────

function SeasonCard({
  recipientLine,
  deadline,
  followsGames,
  zone,
  onDeadline,
  singular,
  plural,
  onSingular,
  onPlural,
}: {
  recipientLine: string;
  deadline: Date | undefined;
  /** The deadline is the last game's day, filled in. */
  followsGames: boolean;
  /** The zone the deadline is set in: the stored one's, else the reader's. */
  zone: string | null | undefined;
  onDeadline: (day: Date | undefined) => void;
  singular: string;
  plural: string;
  onSingular: (value: string) => void;
  onPlural: (value: string) => void;
}) {
  return (
    <Card flush testId="season-card">
      <CardHeader title={S.seasonTitle} subtitle={recipientLine} />
      <CardBody className="flex flex-col gap-20">
        <div className="flex flex-col gap-8">
          <p className="text-s14 font-w600 text-text-strong">{C.deadlineTitle}</p>
          <p className="text-s13 leading-h15 text-text-secondary">{C.deadlineSubtitle}</p>
          <DatePicker
            appearance="saas"
            label={C.deadlineLabel}
            placeholder={C.deadlinePlaceholder}
            value={deadline}
            onValueChange={onDeadline}
            className="mt-4 sm:w-[280px]"
            testId="season-deadline"
          />
          <p
            className="text-s12 leading-h15 text-text-secondary"
            data-testid="season-deadline-zone"
          >
            {followsGames ? `${S.deadlineFromLastGame} ` : ''}
            {C.deadlineZone(zoneName(zone))}
          </p>
        </div>
        <div
          className="flex flex-col gap-8 border-t border-border-subtle pt-16"
          data-testid="season-layer-name"
        >
          <p className="text-s14 font-w600 text-text-strong">{C.layerNameTitle}</p>
          <div className="grid gap-12 sm:max-w-[536px] sm:grid-cols-2">
            <Field
              label={C.layerNameSingular}
              placeholder={C.layerNameSingularDefault}
              value={singular}
              maxLength={LAYER_NAME_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => onSingular(event.target.value)}
              onBlur={(event) => onSingular(collapseLayerName(event.target.value))}
              testId="season-layer-singular"
            />
            <Field
              label={C.layerNamePlural}
              placeholder={C.layerNamePluralDefault}
              value={plural}
              maxLength={LAYER_NAME_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => onPlural(event.target.value)}
              onBlur={(event) => onPlural(collapseLayerName(event.target.value))}
              testId="season-layer-plural"
            />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ── The schedule ────────────────────────────────────────────────────────────────────────────────

const SCHEDULE_COLUMNS =
  'lg:grid lg:grid-cols-[184px_148px_minmax(0,4fr)_minmax(0,5fr)_36px] lg:items-start lg:gap-x-12';

function ScheduleCard({
  games,
  subject,
  shown,
  reasons,
  onChange,
  onRemove,
  onAdd,
}: {
  games: GameRow[];
  subject: string;
  /** A press was made: a half-typed game says what it lacks. */
  shown: boolean;
  reasons: ReadonlyMap<number, string | null>;
  onChange: (key: number, change: Partial<GameRow>) => void;
  onRemove: (game: GameRow) => void;
  onAdd: () => void;
}) {
  const seasonZone = deadlineZone(useFundraisersModule().orgTimeZone);
  const count = games.filter(complete).length;
  return (
    <Card flush testId="season-schedule">
      <CardHeader
        title={C.styleSchedule}
        subtitle={S.scheduleSubtitle}
        actions={
          count > 0 ? (
            <span
              className="text-s13 font-w600 text-text-secondary"
              data-testid="season-game-count"
            >
              {S.gameCount(count)}
            </span>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-12">
        <p className="text-s12 leading-h15 text-text-secondary" data-testid="season-times-in">
          {S.timesIn(zoneName(seasonZone))}
        </p>
        {games.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-border px-16 py-20"
            data-testid="season-no-games"
          >
            <p className="text-s13 font-w600 leading-[20px] text-text-strong">{S.noGames}</p>
            <p className="text-s13 leading-h15 text-text-secondary">{S.noGamesBody}</p>
          </div>
        ) : (
          <div className="flex flex-col">
            <div
              className={cn(
                'hidden border-b border-border-subtle pb-8 font-sans text-s11 font-w600 uppercase tracking-wide text-text-secondary',
                SCHEDULE_COLUMNS,
              )}
              aria-hidden
            >
              <span>{S.colDate}</span>
              <span>{S.colTime}</span>
              <span>{S.colOpponent}</span>
              <span>{S.colPage}</span>
              <span />
            </div>
            <ol
              className="flex flex-col gap-12 pt-12 lg:gap-0 lg:pt-0"
              aria-label={C.styleSchedule}
            >
              {games.map((game, index) => (
                <GameLine
                  key={game.key}
                  index={index}
                  game={game}
                  subject={subject}
                  // After a press, a row holds the act when it is half typed, or when no row is whole.
                  flagged={shown && !complete(game) && (!blank(game) || count === 0)}
                  reason={reasons.has(game.key) ? (reasons.get(game.key) ?? C.layerFailed) : null}
                  onChange={(change) => onChange(game.key, change)}
                  onRemove={() => onRemove(game)}
                />
              ))}
            </ol>
          </div>
        )}
        <div>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
            onClick={onAdd}
            className="max-lg:min-h-44"
            testId="season-add-game"
          >
            {S.addGame}
          </Button>
        </div>
        {/* D-FCM-61's copy: a press with no game typed in full says so, beside each row's own line. */}
        {shown && count === 0 && games.length > 0 && (
          <p className="text-s13 font-w500 text-danger-700" data-testid="season-add-one">
            {S.addAtLeastOne}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function GameLine({
  index,
  game,
  subject,
  flagged,
  reason,
  onChange,
  onRemove,
}: {
  index: number;
  game: GameRow;
  subject: string;
  /** A press found this row holding the act: its line turns from a note into what holds it. */
  flagged: boolean;
  /** Why the act didn't make this game, in the server's words (null: it wasn't refused). */
  reason: string | null;
  onChange: (change: Partial<GameRow>) => void;
  onRemove: () => void;
}) {
  const name = rowName(subject, game);
  const label = name ?? S.gameN(index + 1);
  const incomplete = !complete(game);
  // One look for every date on the page (tester-5's item 1): the deadline's picker, and first pitch chosen
  // from a list of times, never the browser's own date and time boxes, each at the module's one field
  // height (the SaaS h-36). On a wide screen each column's label is its header; on a phone each field
  // carries its own.
  const fieldLabel = 'min-w-0 lg:[&>div>:first-child]:sr-only';
  return (
    <li
      className="rounded-lg border border-border-subtle p-12 lg:rounded-none lg:border-0 lg:border-b lg:px-0 lg:py-10"
      data-testid={`season-game-${game.key}`}
    >
      {/* Phone: the game's name and its remove on top, its fields under them. */}
      <div className="mb-8 flex items-center justify-between gap-8 lg:hidden">
        <span
          className={cn('text-s14 font-w600', name ? 'text-text-strong' : 'text-text-secondary')}
        >
          {keepDayTogether(label)}
        </span>
        <IconButton
          aria-label={C.removeFromList(label)}
          onClick={onRemove}
          className="-mr-6 size-44"
          testId={`season-game-remove-${game.key}`}
        >
          <Icon icon={Trash2} size={16} aria-hidden />
        </IconButton>
      </div>
      <div className={cn('grid grid-cols-2 gap-x-12 gap-y-8', SCHEDULE_COLUMNS)}>
        <div className={cn('col-span-2 lg:col-span-1', fieldLabel)}>
          <DatePicker
            appearance="saas"
            label={S.colDate}
            value={game.day ? dayOf(game.day) : undefined}
            onValueChange={(day) => onChange({ day: day ? dayText(day) : '' })}
            error={flagged && game.day === ''}
            testId={`season-game-date-${game.key}`}
          />
        </div>
        <div className={fieldLabel}>
          <Select
            appearance="saas"
            label={S.colTime}
            options={TIME_OPTIONS}
            value={game.time || null}
            onValueChange={(time) => onChange({ time: time ?? '' })}
            error={flagged && game.time === ''}
            testId={`season-game-time-${game.key}`}
          />
        </div>
        <div className={fieldLabel}>
          <Field
            label={S.colOpponent}
            value={game.opponent}
            maxLength={120}
            error={flagged && game.opponent.trim() === ''}
            onChange={(event) => onChange({ opponent: event.target.value })}
            testId={`season-game-opponent-${game.key}`}
          />
        </div>
        <p
          className={cn(
            'hidden min-w-0 text-s14 leading-[36px] lg:block lg:truncate',
            name ? 'font-w600 text-text-strong' : 'text-text-placeholder',
          )}
          data-testid={`season-game-name-${game.key}`}
        >
          {name ? keepDayTogether(name) : '—'}
        </p>
        <div className="hidden lg:flex lg:h-36 lg:items-center lg:justify-end">
          <IconButton
            aria-label={C.removeFromList(label)}
            onClick={onRemove}
            testId={`season-game-remove-d-${game.key}`}
          >
            <Icon icon={Trash2} size={16} aria-hidden />
          </IconButton>
        </div>
      </div>
      {reason !== null && (
        <p
          className="mt-6 flex items-start gap-6 text-s13 font-w500 leading-h15 text-danger-700"
          data-testid={`season-game-reason-${game.key}`}
        >
          <Icon icon={CircleAlert} size={14} aria-hidden className="mt-2 shrink-0" />
          {reason}
        </p>
      )}
      {/* An incomplete game says so where it is, and isn't counted (tester-5's item 6); after a press it is
          what holds the act. */}
      {incomplete && (
        <p
          className={cn(
            'mt-6 text-s12 font-w500',
            flagged ? 'text-danger-700' : 'text-text-secondary',
          )}
          data-testid={`season-game-flag-${game.key}`}
        >
          {S.finishOrRemove}
        </p>
      )}
    </li>
  );
}

// ── The scoreboard ──────────────────────────────────────────────────────────────────────────────

type TermsSetter = (key: keyof SeasonTerms, value: string) => void;
type TermsRefs = Record<
  'subject' | 'unit' | 'most' | 'rate' | 'cap',
  RefObject<HTMLInputElement | null>
>;

function ScoreboardCard({
  terms,
  set,
  units,
  flagged,
  refs,
}: {
  terms: SeasonTerms;
  set: TermsSetter;
  units: string;
  /** The fields a press found missing. */
  flagged: readonly (keyof SeasonTerms)[];
  refs: TermsRefs;
}) {
  return (
    <Card flush testId="season-scoreboard">
      <CardHeader title={S.scoreboardTitle} subtitle={S.scoreboardSubtitle} />
      <CardBody className="flex flex-col gap-16">
        <div className="grid gap-16 sm:grid-cols-2">
          <Field
            ref={refs.subject}
            label={S.whosCounted}
            value={terms.subject}
            maxLength={120}
            error={flagged.includes('subject')}
            onChange={(event) => set('subject', event.target.value)}
            testId="season-subject"
          />
          <Field
            ref={refs.unit}
            label={S.whatCounted}
            value={terms.unit}
            placeholder={S.unitPlaceholder}
            maxLength={80}
            helper={S.writeItAsOne}
            error={flagged.includes('unit')}
            onChange={(event) => set('unit', event.target.value)}
            testId="season-unit"
          />
          <Field
            ref={refs.most}
            label={units ? S.mostExpected(units) : S.mostExpectedFallback}
            value={terms.most}
            inputMode="numeric"
            helper={S.mostChecked}
            error={flagged.includes('most')}
            onChange={(event) => set('most', event.target.value.replace(/\D/g, ''))}
            testId="season-most"
          />
          <Field
            label={
              <span>
                {S.howItReads} <span className="font-w400 text-text-tertiary">· {S.optional}</span>
              </span>
            }
            value={terms.phrase}
            maxLength={120}
            helper={S.howItReadsHelper}
            onChange={(event) => set('phrase', event.target.value)}
            testId="season-phrase"
          />
        </div>
        {/* Multi-line, as iOS draws it (`PledgeCreatorSourceStep.swift`, `multiline: true`). */}
        <Textarea
          label={S.exactlyWhatCounts}
          value={terms.rule}
          placeholder={S.rulePlaceholder}
          rows={2}
          maxLength={500}
          onChange={(event) => set('rule', event.target.value)}
          testId="season-rule"
        />
        {/* Decision 4 (FCM-BE119): both sides of each game's score, entered by the team's staff. The
            opponent's side is each game's opponent from the schedule, a count no pledge reads. */}
        <div
          className="flex flex-col gap-8 border-t border-border-subtle pt-16"
          data-testid="season-both-sides"
        >
          <p className="text-s13 font-w500 text-text-secondary">{S.bothSidesTitle}</p>
          <ul className="flex flex-col gap-6">
            <li className="text-s14 leading-h15 text-text-strong">
              <span className="font-w600">{terms.subject.trim() || S.yourTeam}</span>{' '}
              <span className="text-s13 text-text-secondary">
                {S.sideTeam(units || S.unitsFallback)}
              </span>
            </li>
            <li className="text-s14 leading-h15 text-text-strong">
              <span className="font-w600">{S.opponentLabel}</span>{' '}
              <span className="text-s13 text-text-secondary">
                {S.sideOpponent(terms.subject.trim() || null, units || S.unitsFallback)}
              </span>
            </li>
          </ul>
        </div>
      </CardBody>
    </Card>
  );
}

// ── The nightly pledge (the season's pledge template) ───────────────────────────────────────────

/** D-FCM-53's up-front line: where a pledge goes, before its terms. */
export function ReachLine({ children, testId }: { children: string; testId: string }) {
  return (
    <div
      className="flex items-start gap-8 rounded-lg border border-brand-100 bg-brand-50 px-12 py-10"
      {...testIdProp(testId)}
    >
      <Icon icon={Route} size={16} aria-hidden className="mt-1 shrink-0 text-brand-700" />
      <p className="text-s13 font-w500 leading-h15 text-text-strong">{children}</p>
    </div>
  );
}

/** A dollar amount typed as text (never a float), its unit trailing. */
export function MoneyField({
  label,
  value,
  onChange,
  trailing,
  helper,
  error = false,
  inputRef,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  trailing?: string;
  helper?: string;
  error?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  testId: string;
}) {
  return (
    <Field
      ref={inputRef}
      label={label}
      value={value}
      inputMode="decimal"
      leading={<span className="text-s14 text-text-secondary">$</span>}
      trailing={
        trailing ? (
          <span className="whitespace-nowrap pr-4 text-s13 text-text-secondary">{trailing}</span>
        ) : undefined
      }
      className="pl-24"
      helper={helper}
      error={error}
      onChange={(event) => onChange(event.target.value.replace(/[^\d.]/g, ''))}
      testId={testId}
    />
  );
}

/** Who sets the rate, and the rate and cap it asks for (the apps' approved creator words). */
export function RateFields({
  terms,
  set,
  unit,
  flagged,
  refs,
  testIdPrefix,
}: {
  terms: Pick<SeasonTerms, 'ratePolicy' | 'rate' | 'cap'>;
  set: (key: 'ratePolicy' | 'rate' | 'cap', value: string) => void;
  unit: string;
  flagged: readonly string[];
  refs?: Pick<TermsRefs, 'rate' | 'cap'>;
  testIdPrefix: string;
}) {
  return (
    <>
      {/* Below lg each choice keeps its 44px tap row (the tile's own floor), so the group takes back the
          row's inner space at its edges: its label sits on the choices as a field's sits on its input,
          and the next field follows at the fields' own gap. */}
      <div className="flex flex-col gap-8 max-lg:-mb-12 max-lg:gap-0">
        <p className="text-s13 font-w500 text-text-secondary">{S.whoSetsTheRate}</p>
        <RadioTileGroup
          value={terms.ratePolicy}
          onValueChange={(value) => set('ratePolicy', value as RatePolicy)}
          aria-label={S.whoSetsTheRate}
          className="gap-0 sm:flex-row sm:flex-wrap sm:gap-x-24 sm:gap-y-4"
          testId={`${testIdPrefix}-rate-policy`}
        >
          <RadioTile
            value="suggested"
            label={S.youSuggest}
            testId={`${testIdPrefix}-rate-suggested`}
          />
          <RadioTile value="fixed" label={S.youSetIt} testId={`${testIdPrefix}-rate-fixed`} />
          <RadioTile
            value="pledgor_choice"
            label={S.theyChoose}
            testId={`${testIdPrefix}-rate-choose`}
          />
        </RadioTileGroup>
      </div>
      <div className="grid gap-16 sm:grid-cols-2">
        {terms.ratePolicy !== 'pledgor_choice' && (
          <MoneyField
            label={terms.ratePolicy === 'suggested' ? S.suggestedRate : S.rate}
            value={terms.rate}
            trailing={S.perUnit(unit)}
            error={flagged.includes('rate')}
            inputRef={refs?.rate}
            onChange={(value) => set('rate', value)}
            testId={`${testIdPrefix}-rate`}
          />
        )}
        <MoneyField
          label={S.capLabel}
          value={terms.cap}
          helper={S.capNote}
          error={flagged.includes('cap')}
          inputRef={refs?.cap}
          onChange={(value) => set('cap', value)}
          testId={`${testIdPrefix}-cap`}
        />
      </div>
    </>
  );
}

/** "Pledgors will see": the server's own sentence, or its refusal of these words, in its own words. */
export function PreviewBlock({
  preview,
  previewing,
  refusal,
  failed,
  heading,
  testIdPrefix,
}: {
  preview: PledgeRuleSentencePreviewOut | null;
  /** The terms are complete enough to ask for a sentence. */
  previewing: boolean;
  refusal: string | null;
  failed: boolean;
  /** A line naming whose page the sentence is (the template card's), above the sentence. */
  heading?: string | null;
  testIdPrefix: string;
}) {
  const lines = previewing ? previewLines(preview) : null;
  if (lines)
    return (
      <div className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-12">
        {heading && <p className="text-s13 leading-h15 text-text-secondary">{heading}</p>}
        <p
          className="text-s14 font-w700 leading-h15 text-text-strong"
          data-testid={`${testIdPrefix}-preview-sentence`}
        >
          {lines.sentence}
        </p>
        {lines.whatCounts && (
          <p className="text-s13 leading-h15 text-text-strong">{lines.whatCounts}</p>
        )}
        {lines.precision && (
          <p className="text-s13 leading-h15 text-text-secondary">{lines.precision}</p>
        )}
      </div>
    );
  if (previewing && refusal)
    return (
      <p
        className="flex items-start gap-6 text-s13 font-w500 leading-h15 text-danger-700"
        data-testid={`${testIdPrefix}-preview-refused`}
      >
        <Icon icon={CircleAlert} size={14} aria-hidden className="mt-2 shrink-0" />
        {refusal}
      </p>
    );
  if (previewing && failed)
    return <p className="text-s13 leading-h15 text-text-secondary">{S.previewUnavailable}</p>;
  return (
    <p
      className="text-s13 leading-h15 text-text-tertiary"
      data-testid={`${testIdPrefix}-preview-waiting`}
    >
      —
    </p>
  );
}

function NightlyCard({
  season,
  terms,
  set,
  flagged,
  refs,
  recipients,
  preview,
  previewing,
  previewRefusal,
  previewFailed,
}: {
  season: string;
  terms: SeasonTerms;
  set: TermsSetter;
  flagged: readonly (keyof SeasonTerms)[];
  refs: TermsRefs;
  recipients: ReactNode;
  preview: PledgeRuleSentencePreviewOut | null;
  previewing: boolean;
  /** The preview's own refusal of these words, in the server's sentence (it screens as a create does). */
  previewRefusal: string | null;
  previewFailed: boolean;
}) {
  const unit = terms.unit.trim() || 'point';
  return (
    <Card flush testId="season-nightly">
      <CardHeader title={S.nightlyTitle} />
      <CardBody className="flex flex-col gap-20">
        <ReachLine testId="season-reach">{S.reachSeason(season)}</ReachLine>
        <div className="flex flex-col gap-6">
          <p className="text-s13 font-w500 text-text-secondary">{S.howTheAmountWorks}</p>
          <p className="text-s14 font-w600 text-text-strong" data-testid="season-for-every">
            {S.forEvery(unit)}
          </p>
        </div>
        <RateFields
          terms={terms}
          set={set}
          unit={unit}
          flagged={flagged}
          refs={refs}
          testIdPrefix="season"
        />
        {/* D-GFU-57: pledging opens when each game's pledge is published, so only its close is said. */}
        <div className="flex flex-col gap-6" data-testid="season-closes">
          <p className="text-s13 font-w500 text-text-secondary">{S.pledgingCloses}</p>
          <p className="text-s14 font-w600 leading-[20px] text-text-strong">{S.closesAtFinalOut}</p>
          <p className="text-s12 leading-h15 text-text-secondary">{S.closesAtFinalOutHelper}</p>
        </div>
        <div className="border-t border-border-subtle pt-16">{recipients}</div>
        <div
          className="flex flex-col gap-8 border-t border-border-subtle pt-16"
          data-testid="season-preview"
        >
          {previewing && (
            <>
              <p className="text-s13 font-w500 text-text-secondary">{S.pledgorsWillSee}</p>
              <PreviewBlock
                preview={preview}
                previewing={previewing}
                refusal={previewRefusal}
                failed={previewFailed}
                testIdPrefix="season"
              />
            </>
          )}
          <p
            className="flex items-start gap-6 text-s13 leading-h15 text-text-secondary"
            data-testid="season-featured-note"
          >
            <Icon icon={IdCard} size={14} aria-hidden className="mt-2 shrink-0" />
            {S.featuredBySetup}
          </p>
          <p className="text-s12 leading-h15 text-text-secondary">{S.termsFixed}</p>
        </div>
      </CardBody>
    </Card>
  );
}

/** Who each nightly fundraiser raises for: the charity itself (Q8), the season's own charities where it
 * picks them, or picks from the list that binds where its supporters pick (any charity without one). */
function Recipients({
  selfName,
  ownItems,
  listFrom,
  picksOwn,
  picks,
  listItems,
  listReady,
  flagged,
  boxRef,
  onPick,
  onUnpick,
}: {
  selfName: string | null;
  /** The season's own charities, where the season picks them; null where its supporters pick. */
  ownItems: RecipientItem[] | null;
  /** The campaign whose list binds the picks, if one does. */
  listFrom: string | null;
  picksOwn: boolean;
  picks: RecipientItem[];
  listItems: RecipientItem[];
  listReady: boolean;
  /** A press found no pick. */
  flagged: boolean;
  boxRef: RefObject<HTMLDivElement | null>;
  onPick: (item: RecipientItem) => void;
  onUnpick: (item: RecipientItem) => void;
}) {
  const pickedKeys = new Set(picks.map(recipientKey));
  return (
    <div className="flex flex-col gap-8" data-testid="season-recipients">
      <p className="text-s13 font-w500 text-text-secondary">{S.whoReceives}</p>
      {selfName !== null ? (
        <div className="flex items-center gap-8">
          <Avatar name={selfName} size="sm" />
          <span className="text-s14 font-w600 text-text-strong">{selfName}</span>
        </div>
      ) : !picksOwn ? (
        ownItems && ownItems.length > 0 && <Chips items={ownItems} testId="season-own" />
      ) : (
        <>
          {listFrom && (
            <p className="text-s13 leading-h15 text-text-secondary">{C.listFromAbove(listFrom)}</p>
          )}
          {picks.length > 0 && <Chips items={picks} testId="season-picks" onRemove={onUnpick} />}
          <div
            ref={boxRef}
            className={cn(
              'flex flex-col rounded-lg border p-12',
              listFrom && 'max-h-[200px] overflow-y-auto',
              flagged ? 'border-danger-500' : 'border-border-subtle',
            )}
            data-testid="season-pick-box"
          >
            {listFrom ? (
              <PickerResults
                loading={!listReady}
                error={false}
                results={listItems
                  .filter((item) => !pickedKeys.has(recipientKey(item)))
                  .map((item) => ({ ...item, subtitle: null }))}
                addedIds={pickedKeys}
                adding={false}
                onAdd={(result) => onPick({ ...result })}
                onRetry={() => undefined}
                testIdPrefix="season-recipient"
              />
            ) : (
              <RecipientSearch
                addedIds={pickedKeys}
                adding={false}
                onAdd={(result) => onPick({ ...result })}
                testIdPrefix="season-recipient"
                compact
              />
            )}
          </div>
        </>
      )}
      <p className="text-s12 leading-h15 text-text-secondary">{S.recipientsNote}</p>
    </div>
  );
}

// ── The campaign card (FCM-BE56, FCM-BE66: the season setup picks Schedule) ────────────────────

function CardLevelCard({ season }: { season: string }) {
  return (
    <Card flush testId="season-card-level">
      <CardHeader title={C.cardTitle} subtitle={C.cardLine(season)} />
      <CardBody className="flex flex-col gap-8" testId="season-card-style">
        <div>
          <p className="text-s14 font-w600 text-text-strong">{C.styleSchedule}</p>
          <p className="mt-2 text-s13 leading-h15 text-text-secondary">{C.styleScheduleDesc}</p>
        </div>
        <p className="text-s12 leading-h15 text-text-secondary">{S.cardSetOnce}</p>
      </CardBody>
    </Card>
  );
}

/** A stored template's terms as rows: whose count, what, what counts, the rate, the cap, when it closes. */
export function TemplateTermsList({
  template,
  testId,
}: {
  template: PledgeTemplateOut;
  testId: string;
}) {
  const { measure, offer } = template;
  const named = (measure.subject_name ?? '').trim() !== '';
  const rows: [string, string][] = [
    [S.whosCounted, named ? measure.subject_name! : S.eachJoiner],
    [S.whatCounted, measure.unit_label],
    ...((measure.qualifying_rule ?? '').trim() !== ''
      ? [[S.exactlyWhatCounts, measure.qualifying_rule!] as [string, string]]
      : []),
    // A rate per unit only: a template the card can't make (a flat amount) is never worded as one.
    ...(offer.amount_rule !== 'per_unit'
      ? []
      : offer.rate_policy === 'pledgor_choice'
        ? [[S.whoSetsTheRate, S.theyChoose] as [string, string]]
        : offer.suggested_rate_cents != null
          ? [
              [
                offer.rate_policy === 'fixed' ? S.rate : S.suggestedRate,
                `${centsShown(offer.suggested_rate_cents)} ${S.perUnit(measure.unit_label)}`,
              ] as [string, string],
            ]
          : []),
    [S.startingCap, centsShown(offer.default_cap_cents)],
    ...(offer.window === 'at_end'
      ? [[S.pledgingCloses, S.closesAtFinalOut] as [string, string]]
      : []),
  ];
  return (
    <dl
      className="grid gap-x-16 gap-y-10 text-s13 sm:grid-cols-[max-content_1fr]"
      data-testid={testId}
    >
      {rows.map(([term, value]) => (
        <div key={term} className="contents">
          <dt className="text-text-secondary">{term}</dt>
          <dd className="font-w600 text-text-strong">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── "Add games": the season's pledge as stored (tester-5's item 9) ───────────────────────────────

/** The pledge the new games take: the season's template as stored, in its own words and the server's
 * sentence, under the Campaign tab's own title for it. Read-only: the template is changed there. */
function StoredPledgeCard({
  season,
  template,
  preview,
  previewing,
  previewFailed,
}: {
  season: string;
  template: PledgeTemplateOut;
  preview: PledgeRuleSentencePreviewOut | null;
  previewing: boolean;
  previewFailed: boolean;
}) {
  return (
    <Card flush testId="season-stored-pledge">
      <CardHeader title={S.templateTitle} subtitle={S.templateSubtitle} />
      <CardBody className="flex flex-col gap-20">
        <ReachLine testId="season-reach">{S.reachSeason(season)}</ReachLine>
        <TemplateTermsList template={template} testId="season-stored-terms" />
        {previewing && (
          <div
            className="flex flex-col gap-8 border-t border-border-subtle pt-16"
            data-testid="season-preview"
          >
            <p className="text-s13 font-w500 text-text-secondary">{S.pledgorsWillSee}</p>
            <PreviewBlock
              preview={preview}
              previewing
              refusal={null}
              failed={previewFailed}
              testIdPrefix="season"
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── What it sets up, the Terms of Use, and the act ──────────────────────────────────────────────

function SetupSummary({
  season,
  units,
  count,
  rows,
  adding,
  busy,
  partly,
  refusal,
  terms,
  termsState,
  onTermsRetry,
  onTermsOpen,
  onSave,
  onCancel,
}: {
  season: string;
  /** The scoreboard's unit, as the scoreboard card names it. */
  units: string;
  /** The games typed in full. */
  count: number;
  /** The schedule's rows, typed in full or not: with none, there is nothing to press for. */
  rows: number;
  adding: boolean;
  busy: boolean;
  /** Some games are made; the rest wait to be tried again. */
  partly: boolean;
  /** A refusal that stopped the whole act, in the server's words, until the form changes. */
  refusal: string | null;
  terms: CurrentTerms | null;
  termsState: 'loading' | 'failed' | 'ready';
  onTermsRetry: () => void;
  onTermsOpen: () => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const lines = [
    S.summaryGames(count, season),
    S.summaryNightly,
    S.summaryScoreboard(units),
    // The act sets the season's card on its first setup only; later games keep it as last set.
    ...(adding ? [] : [S.summaryCard(season)]),
  ];
  return (
    <Card flush testId="season-summary">
      <CardHeader title={S.summaryTitle} />
      <CardBody className="flex flex-col gap-16">
        <ul className="flex flex-col gap-8">
          {lines.map((line) => (
            <li key={line} className="flex items-start gap-8 text-s13 leading-h15 text-text-strong">
              <Icon
                icon={CircleCheck}
                size={14}
                aria-hidden
                className="mt-2 shrink-0 text-brand-600"
              />
              {line}
            </li>
          ))}
        </ul>
        {/* D-FCM-65: the act records the organizer's agreement to the Terms shown, just above it. */}
        <div className="flex flex-col gap-4" data-testid="season-terms">
          <p className="text-s12 leading-h15 text-text-secondary">
            {adding ? S.termsBeforeAdding : S.termsBefore}
            <button
              type="button"
              onClick={onTermsOpen}
              disabled={terms === null}
              className={cn(
                INLINE_HIT,
                'font-w600 text-brand-600 underline-offset-2 outline-none hover:underline focus-visible:underline disabled:cursor-default disabled:font-w400 disabled:text-text-secondary disabled:no-underline',
              )}
              data-testid="season-terms-open"
            >
              {S.termsLink}
            </button>
            {S.termsAfter}
          </p>
          {termsState === 'loading' && (
            <p className="text-s12 leading-h15 text-text-secondary">{S.termsLoading}</p>
          )}
          {termsState === 'failed' && (
            <p className="text-s12 leading-h15 text-danger-700" data-testid="season-terms-failed">
              {S.termsFailed}{' '}
              <button
                type="button"
                onClick={onTermsRetry}
                className={cn(
                  INLINE_HIT,
                  'font-w600 text-brand-600 outline-none hover:underline focus-visible:underline',
                )}
                data-testid="season-terms-retry"
              >
                {S.tryAgain}
              </button>
            </p>
          )}
        </div>
        {busy && (
          // Decision 6 (FCM-BE119): one act; the page waits once. Beside the spinner it explains
          // (tester-5's item 7).
          <p
            className="text-s13 font-w500 leading-h15 text-text-secondary"
            role="status"
            data-testid="season-progress"
          >
            {S.settingUp(count)} {S.takesAMinute}
          </p>
        )}
        {refusal && !busy && (
          <p
            className="flex items-start gap-6 text-s13 font-w500 leading-h15 text-danger-700"
            role="status"
            data-testid="season-refusal"
          >
            <Icon icon={CircleAlert} size={14} aria-hidden className="mt-2 shrink-0" />
            {refusal}
          </p>
        )}
        <div className="flex flex-col gap-8">
          {/* Held only while saving, with no Terms to agree to, or with no row: a press with rows not yet
              typed in full says which (D-FCM-61's copy, review-88's F4). */}
          <Button
            appearance="saas"
            loading={busy}
            disabled={busy || rows === 0 || terms === null}
            onClick={onSave}
            testId="season-submit"
          >
            {partly && count > 0 ? S.tryAgain : adding ? S.addGames : S.submit}
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={busy}
            onClick={onCancel}
            testId="season-cancel"
          >
            {partly ? S.partlyDone : S.cancel}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** The Terms of Use the line opens, as plain text (never read as markup), with their version. */
function TermsReader({
  terms,
  open,
  onOpenChange,
}: {
  terms: CurrentTerms;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <DialogFrame
      open={open}
      onOpenChange={onOpenChange}
      title={S.termsLink}
      testId="season-terms-reader"
      width={640}
      sticky
      footer={
        <Button appearance="saas" onClick={() => onOpenChange(false)} testId="season-terms-done">
          {S.termsDone}
        </Button>
      }
    >
      <p className="text-s12 leading-h15 text-text-secondary" data-testid="season-terms-version">
        {S.termsVersion(terms.version)}
      </p>
      <p
        className="whitespace-pre-wrap text-s14 leading-h15 text-text-strong"
        data-testid="season-terms-body"
      >
        {terms.text}
      </p>
    </DialogFrame>
  );
}
